import { ApplicationInsights } from '@microsoft/applicationinsights-web';

/**
 * Application Insights initialisation for the WFP SPA.
 *
 * Reads VITE_APPINSIGHTS_CONNECTION_STRING from the Vite environment. If
 * unset (typical posture for local dev without an AI resource configured),
 * the module is a no-op — initAppInsights returns null and every helper
 * is a safe no-op when called with a null instance.
 *
 * Sets cloud_RoleName to 'wfp-spa' so cross-service queries against the
 * shared nash-reporting-api-nz-insights resource can filter SPA traffic
 * apart from the WFP API (wfp-api) and other tenants.
 *
 * Adds a telemetry initializer that scrubs numeric ID segments from page
 * view URIs, dependency target URLs, and any name field that looks like
 * a path. Without this, whānau IDs accumulate in operation name facets
 * and bleed identifiable information into telemetry.
 *
 * Whānau data is sensitive — by CLAUDE.md convention we don't log it.
 * Telemetry carries identifiers (whanau_id, session_id, kaimahi_oid) and
 * action names only; never names, notes, or any sensitive content.
 */

const ROLE_NAME = 'wfp-spa';

let cached: ApplicationInsights | null = null;

export function initAppInsights(): ApplicationInsights | null {
  if (cached) return cached;

  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING as
    | string
    | undefined;

  if (!connectionString) {
    // Local dev without telemetry — no-op posture.
    return null;
  }

  const instance = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: false,
      enableResponseHeaderTracking: false,
      disableFetchTracking: false,
      enableUnhandledPromiseRejectionTracking: true,
      autoTrackPageVisitTime: true,
    },
  });

  instance.loadAppInsights();

  instance.addTelemetryInitializer((envelope) => {
    // Cloud role tag — separates SPA telemetry from API and other tenants.
    if (envelope.tags) {
      envelope.tags['ai.cloud.role'] = ROLE_NAME;
    } else {
      envelope.tags = { 'ai.cloud.role': ROLE_NAME };
    }

    // URL / operation-name scrubbing. PageView, RemoteDependency, and
    // Exception telemetry all carry path-like fields. Scrub anywhere we
    // see a numeric ID segment so /whanau/91/profile collapses into
    // /whanau/{id}/profile in the operation index.
    const baseData = (envelope.baseData ?? {}) as Record<string, unknown>;
    if (typeof baseData.uri === 'string') {
      baseData.uri = scrubPath(baseData.uri);
    }
    if (typeof baseData.target === 'string') {
      baseData.target = scrubPath(baseData.target);
    }
    if (typeof baseData.name === 'string' && baseData.name.startsWith('/')) {
      baseData.name = scrubPath(baseData.name);
    }
    if (typeof baseData.data === 'string' && baseData.data.startsWith('http')) {
      baseData.data = scrubPath(baseData.data);
    }
    return true;
  });

  cached = instance;
  return instance;
}

/**
 * Replace numeric path segments with {id} placeholders so that
 * /whanau/91/profile becomes /whanau/{id}/profile. WFP identifiers are
 * all numeric — a single pattern covers every per-record route and
 * dependency call without per-route configuration.
 */
function scrubPath(path: string): string {
  return path.replace(/\/\d+(?=\/|$|\?)/g, '/{id}');
}

/**
 * Fire a custom event. Safe to call when AI is not configured — becomes
 * a no-op. Properties are stringified to match the SDK's expectations.
 *
 * Pass identifiers and action names only — never display names, notes,
 * or any other content listed as sensitive in CLAUDE.md.
 */
export function trackEvent(
  instance: ApplicationInsights | null,
  name: string,
  properties: Record<string, string | number | boolean> = {},
): void {
  if (!instance) return;
  const stringProps: Record<string, string> = {};
  for (const [k, v] of Object.entries(properties)) {
    stringProps[k] = String(v);
  }
  instance.trackEvent({ name }, stringProps);
}

export function trackException(
  instance: ApplicationInsights | null,
  error: Error,
  properties: Record<string, string | number | boolean> = {},
): void {
  if (!instance) return;
  const stringProps: Record<string, string> = {};
  for (const [k, v] of Object.entries(properties)) {
    stringProps[k] = String(v);
  }
  instance.trackException({ exception: error, properties: stringProps });
}

/**
 * Set the authenticated user context. Call after MSAL sign-in completes
 * with the kaimahi OID so SPA telemetry correlates with API telemetry
 * by user. The third arg (storeInCookie) is true so the context survives
 * page reloads.
 */
export function setTelemetryUser(
  instance: ApplicationInsights | null,
  oid: string,
): void {
  if (!instance) return;
  instance.setAuthenticatedUserContext(oid, undefined, true);
}

export function clearTelemetryUser(
  instance: ApplicationInsights | null,
): void {
  if (!instance) return;
  instance.clearAuthenticatedUserContext();
}
